import axiosInstance from './index';

// axiosInstance.baseURL already ends in /manage, so paths here are relative to it
// and the shared auth-header / jwt-expiry interceptors apply automatically.

export const getIntegrationByAlias = (alias, { signal } = {}) =>
  axiosInstance.get(`/integrations/${encodeURIComponent(alias)}`, { signal });

export const getIntegrationObjects = (integrationId, { signal } = {}) =>
  axiosInstance.get(`/integrations/${encodeURIComponent(integrationId)}/objects`, { signal });

export const getIntegrationObjectRows = (integrationId, objectName, { limit = 10, page = 1, signal } = {}) =>
  axiosInstance.get(
    `/integrations/${encodeURIComponent(integrationId)}/objects/${encodeURIComponent(objectName)}/rows`,
    { params: { limit, page }, signal }
  );

// ─── Normalizers ─────────────────────────────────────────────────────────────
// The control plane wraps payloads inconsistently across endpoints, so unwrap
// defensively the same way handleEditNode does for activity templates.

const unwrap = (res, ...keys) => {
  const body = res?.data ?? res;
  for (const key of keys) {
    if (body && body[key] !== undefined && body[key] !== null) return body[key];
  }
  return body;
};

const SECRET_KEY_PATTERN = /pass(word)?|secret|key|token|credential|auth|cert|private/i;
export const MASKED_VALUE = '••••••••';

/**
 * Masks credential-bearing entries before the object ever reaches React state,
 * so secrets are never held in memory by the view layer. View-only screen —
 * nothing round-trips, so there is no reveal affordance.
 */
export const maskSecrets = (connection) => {
  if (!connection || typeof connection !== 'object' || Array.isArray(connection)) return connection;
  return Object.entries(connection).reduce((acc, [key, value]) => {
    acc[key] = SECRET_KEY_PATTERN.test(key) && value !== null && value !== undefined && value !== ''
      ? MASKED_VALUE
      : value;
    return acc;
  }, {});
};

export const normalizeIntegration = (res) => {
  const data = unwrap(res, 'integration', 'data') || {};
  return {
    _id: data._id || data.id || null,
    alias: data.integration_alias || data.alias || data.integrationName || data.displayName || '',
    displayName: data.displayName || data.integrationName || '',
    provider: data.provider || data.integrationType || '',
    status: data.status || '',
    connection: maskSecrets(data.config?.connection || data.connection || null),
  };
};

/**
 * Object listing shape is not contractually pinned, so accept the plausible
 * variants: bare strings, objects under a few common key spellings, or either
 * of those wrapped in { objects } / { data }.
 */
export const normalizeObjects = (res) => {
  const raw = unwrap(res, 'objects', 'data');
  const list = Array.isArray(raw) ? raw : Array.isArray(raw?.objects) ? raw.objects : [];
  return list
    .map((item) => {
      if (typeof item === 'string') {
        return { name: item, label: null, schema: null, type: null, rowCount: null };
      }
      if (!item || typeof item !== 'object') return null;
      const name = item.name || item.objectName || item.object_name || item.table_name || item.tableName;
      if (!name) return null;
      return {
        // `name` stays the stable identifier used for API calls and selection;
        // `label` (e.g. a Google Drive file's display name) is display-only —
        // callers must fall back to `name` wherever there is no label.
        name,
        label: item.label || null,
        schema: item.schema || item.schema_name || item.schemaName || null,
        type: item.type || item.object_type || item.objectType || null,
        rowCount: item.rowCount ?? item.row_count ?? null,
      };
    })
    .filter(Boolean);
};

/**
 * Pagination envelope used across the control plane's collection endpoints:
 * { page, limit, total, pages }. The rows endpoint is not guaranteed to send
 * it, so callers must cope with null (see hasMore handling in the explorer).
 */
const normalizePagination = (body) => {
  const p = body?.pagination;
  if (!p || typeof p !== 'object') return null;
  const pages = Number(p.pages ?? p.totalPages);
  const total = Number(p.total ?? p.totalCount);
  return {
    page: Number(p.page) || 1,
    limit: Number(p.limit) || null,
    total: Number.isFinite(total) ? total : null,
    pages: Number.isFinite(pages) ? pages : null,
  };
};

/**
 * Rows endpoint returns { columns: [{name, dataType, nullable, hasDefault}], rows: [...] }
 * and, when paging, a { pagination } envelope.
 * Falls back to deriving columns from the first row when metadata is absent.
 */
export const normalizeRows = (res) => {
  const body = unwrap(res, 'data');
  const rows = Array.isArray(body?.rows) ? body.rows : Array.isArray(body) ? body : [];
  let columns = Array.isArray(body?.columns)
    ? body.columns.map((col) =>
        typeof col === 'string'
          ? { name: col, dataType: null, nullable: null, hasDefault: null }
          : {
              name: col.name || col.column_name || col.columnName || '',
              dataType: col.dataType || col.data_type || null,
              nullable: col.nullable ?? null,
              hasDefault: col.hasDefault ?? col.has_default ?? null,
            }
      ).filter((col) => col.name)
    : [];
  if (!columns.length && rows.length && rows[0] && typeof rows[0] === 'object') {
    columns = Object.keys(rows[0]).map((name) => ({
      name,
      dataType: null,
      nullable: null,
      hasDefault: null,
    }));
  }
  return { columns, rows, pagination: normalizePagination(body) };
};

/**
 * Pulls a human-usable message out of an axios failure, preferring whatever the
 * control plane said over the generic "Request failed with status code 500".
 */
export const integrationErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;
